import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable/index';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';

type UserType = 'user' | 'agency' | 'developer';

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [userType, setUserType] = useState<UserType>('user');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // All user types register with user_type in metadata
    // Professional users (agent/agency/developer) register as 'user' role initially
    // and must complete an application + get approved before getting their professional role
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, phone, user_type: userType },
        emailRedirectTo: window.location.origin,
      },
    });
    setLoading(false);

    if (error) {
      toast({ variant: 'destructive', title: 'Registration failed', description: error.message });
      return;
    }

    if (userType === 'agency' || userType === 'developer') {
      const redirectMap: Record<string, string> = {
        developer: '/developer-application',
        agency: '/agency-application',
      };
      toast({
        title: 'Account created!',
        description: 'Please verify your email, then complete your application with the required documents.',
      });
      navigate(redirectMap[userType]);
    } else {
      // Note: 'agent' user_type removed — agents are created by their agency, not via self-signup.
      toast({ title: 'Account created!', description: 'Please check your email to verify your account.' });
      navigate('/login');
    }
  };

  const userTypes: { value: UserType; label: string; description: string }[] = [
    { value: 'user', label: t('auth.user'), description: 'Browse & save properties' },
    { value: 'agency', label: 'Agency', description: 'Manage agents & listings' },
    { value: 'developer', label: t('auth.developer'), description: 'Showcase projects' },
  ];

  return (
    <Layout>
      <div className="container flex items-center justify-center min-h-[calc(100vh-12rem)] py-8">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="font-display text-2xl">{t('auth.register')}</CardTitle>
            <CardDescription>
              {t('auth.alreadyHaveAccount')}{' '}
              <Link to="/login" className="text-primary font-medium hover:underline">{t('auth.login')}</Link>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label>{t('auth.registerAs')}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {userTypes.map(ut => (
                    <button
                      key={ut.value}
                      type="button"
                      onClick={() => setUserType(ut.value)}
                      className={`py-2.5 px-3 rounded-xl text-left border transition-all ${
                        userType === ut.value
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-secondary text-muted-foreground hover:border-primary/50'
                      }`}
                    >
                      <div className="text-xs font-semibold">{ut.label}</div>
                      <div className={`text-[10px] mt-0.5 ${userType === ut.value ? 'text-primary-foreground/80' : 'text-muted-foreground/70'}`}>{ut.description}</div>
                    </button>
                  ))}
                </div>
                {(userType === 'agency' || userType === 'developer') && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg mt-1">
                    ⚠️ {userType === 'agency' ? 'Agencies' : 'Developers'} must submit required documents and get approved by admin before accessing the full dashboard.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="fullName">{t('auth.fullName')}</Label>
                <Input id="fullName" required value={fullName} onChange={e => setFullName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{t('auth.phone')}</Label>
                <Input id="phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Input id="password" type="password" required minLength={6} value={password} onChange={e => setPassword(e.target.value)} />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? '...' : t('auth.register')}
              </Button>
            </form>
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-xs text-muted-foreground uppercase">{t('auth.or', 'Or')}</span>
                <Separator className="flex-1" />
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={async () => {
                  const result = await lovable.auth.signInWithOAuth("google", {
                    redirect_uri: window.location.origin,
                  });
                  if (result.error) {
                    toast({ variant: 'destructive', title: 'Google sign-up failed', description: String(result.error) });
                  }
                }}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                {t('auth.googleSignUp', 'Sign up with Google')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
